import { useState } from "react";
import { Button } from "../components/ui/button";
import { GRAND_BOUTON, TitreEcran } from "./commun";
import type { Jeu } from "./jeu";
import { messageDeReprise } from "./textes";

/** Le code ecrit sous le QR code, tape a la main. */
export function SaisieCode({
  onCode,
  onFermer,
}: {
  onCode: (code: string) => void;
  onFermer: () => void;
}) {
  const [code, setCode] = useState("");
  return (
    <section aria-labelledby="titre-saisie" className="flex flex-col gap-4">
      <TitreEcran id="titre-saisie" className="text-xl font-semibold">
        Taper un code
      </TitreEcran>
      <form
        className="flex flex-col gap-3"
        onSubmit={(evenement) => {
          evenement.preventDefault();
          if (code.trim() !== "") onCode(code);
        }}
      >
        <label htmlFor="code-choix">Tapez les trois caractères écrits sous le QR code :</label>
        <input
          id="code-choix"
          value={code}
          onChange={(evenement) => setCode(evenement.target.value.toUpperCase())}
          maxLength={5}
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck={false}
          className="h-16 rounded-md border border-input bg-background px-4 text-center font-mono text-3xl uppercase tracking-[0.4em]"
        />
        <Button type="submit" className={GRAND_BOUTON} disabled={code.trim() === ""}>
          Valider le code
        </Button>
      </form>
      <Button variant="ghost" onClick={onFermer}>
        Annuler
      </Button>
    </section>
  );
}

/** Le code de sauvegarde, a recopier sur la feuille d'aventure. */
export function Sauvegarde({ code, onFermer }: { code: string; onFermer: () => void }) {
  return (
    <section aria-labelledby="titre-sauvegarde" className="flex flex-col gap-4">
      <TitreEcran id="titre-sauvegarde" className="text-xl font-semibold">
        Code de sauvegarde
      </TitreEcran>
      <p
        data-testid="code-de-sauvegarde"
        className="rounded-lg border border-border bg-card p-4 text-center font-mono text-3xl font-semibold tracking-widest"
      >
        {code}
      </p>
      <p>
        Notez-le sur la feuille d'aventure. Avec lui, vous reprendrez la partie plus tard ou sur un
        autre téléphone : ce navigateur peut oublier votre partie si vous ne revenez pas pendant
        plusieurs jours.
      </p>
      <Button className={GRAND_BOUTON} onClick={onFermer}>
        C'est noté
      </Button>
    </section>
  );
}

/** Reprendre une partie a partir de son code de sauvegarde. */
export function Reprise({
  jeu,
  pageEnCours,
  onFermer,
}: {
  jeu: Jeu;
  pageEnCours: number | null;
  onFermer: () => void;
}) {
  const [code, setCode] = useState("");
  const [erreur, setErreur] = useState<string | null>(null);
  return (
    <section aria-labelledby="titre-reprise" className="flex flex-col gap-4">
      <TitreEcran id="titre-reprise" className="text-xl font-semibold">
        Reprendre avec un code
      </TitreEcran>
      <form
        className="flex flex-col gap-3"
        onSubmit={(evenement) => {
          evenement.preventDefault();
          const reprise = jeu.reprendre(code);
          if (reprise.ok) onFermer();
          else setErreur(messageDeReprise(reprise.refus));
        }}
      >
        <label htmlFor="code-sauvegarde">Votre code de sauvegarde (XXXX-XXXX-X) :</label>
        <input
          id="code-sauvegarde"
          value={code}
          onChange={(evenement) => {
            setCode(evenement.target.value.toUpperCase());
            setErreur(null);
          }}
          maxLength={14}
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck={false}
          aria-invalid={erreur !== null}
          aria-describedby={erreur ? "erreur-reprise" : undefined}
          className="h-14 rounded-md border border-input bg-background px-4 text-center font-mono text-2xl uppercase tracking-widest"
        />
        {erreur ? (
          <p id="erreur-reprise" role="alert" className="text-sm text-destructive">
            {erreur}
          </p>
        ) : null}
        {pageEnCours !== null ? (
          <p className="text-sm text-muted-foreground">
            Votre partie en cours (page {pageEnCours}) sera remplacée.
          </p>
        ) : null}
        <Button type="submit" className={GRAND_BOUTON} disabled={code.trim() === ""}>
          Reprendre la partie
        </Button>
      </form>
      <Button variant="ghost" onClick={onFermer}>
        Annuler
      </Button>
    </section>
  );
}

/** Recommencer efface la partie : le code de sauvegarde est rappele avant, pour y revenir. */
export function Recommencer({
  jeu,
  page,
  onFermer,
}: {
  jeu: Jeu;
  page: number;
  onFermer: () => void;
}) {
  const code = jeu.codeActuel();
  return (
    <section aria-labelledby="titre-recommencer" className="flex flex-col gap-4">
      <TitreEcran id="titre-recommencer" className="text-xl font-semibold">
        Recommencer la partie ?
      </TitreEcran>
      <p>Cela efface de ce téléphone votre partie en cours (page {page}) et votre carnet.</p>
      {code ? (
        <p>
          Pour pouvoir y revenir, notez d'abord son code de sauvegarde :{" "}
          <span className="font-mono font-semibold tracking-wider">{code}</span>
        </p>
      ) : null}
      <Button
        className={GRAND_BOUTON}
        onClick={() => {
          jeu.recommencer();
          onFermer();
        }}
      >
        Recommencer depuis la page 1
      </Button>
      <Button variant="outline" className={GRAND_BOUTON} onClick={onFermer}>
        Continuer ma partie
      </Button>
    </section>
  );
}
